import { Component, computed, OnInit } from '@angular/core';
import { MatCard, MatCardContent } from '@angular/material/card';
import { combineLatest, skip, take } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DynamicObservable } from '../../../../core/classes/dynamic-observable';
import { DatatableComponent } from '../../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../../shared/components/datatable/datatable.interface';
import { IBaseDatatableState } from '../../../../state/base-datatable-store.service';
import { FormUtilitiesService } from '../../../../utilities/form-utilities.service';
import { BaseCrudPageComponent } from '../../../app/base-crud-page.component';
import { FormAutofillAddEditDialogComponent } from './form-autofill-add-edit-dialog/form-autofill-add-edit-dialog.component';
import { IFormAutofillAddEditDialogData } from './form-autofill-add-edit-dialog/form-autofill-add-edit-dialog.interface';
import { IFormAutofill, IFormAutofillDatatableRow } from './form-autofill.interface';
import { FormAutofillStore } from './form-autofill.store';

@Component({
  imports: [DatatableComponent, MatCard, MatCardContent],
  providers: [FormAutofillStore],
  selector: 'app-form-autofill',
  templateUrl: './form-autofill.component.html',
})
export class FormAutofillComponent
  extends BaseCrudPageComponent<IFormAutofill, IBaseDatatableState<IFormAutofill>>
  implements OnInit
{
  public readonly formattedDatatableRows = computed(() => {
    return this.store.data$().map(({ id, name, form }) => ({
      form: form?.name,
      id,
      name,
    }));
  });

  public readonly datatableColumns: IDatatableColumn<IFormAutofillDatatableRow>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
    { id: 'form', isSortable: true },
  ];

  public datatableConfigurations: IDatatableConfiguration<IFormAutofillDatatableRow> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.bulk,
    editScope: ETableCrudScope.single,
  };

  private readonly afterCreateUpdateSuccess = new DynamicObservable<unknown>(null);

  constructor(
    public readonly store: FormAutofillStore,
    public readonly formService: FormUtilitiesService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfFormAutofill',
      multiContext: 'field.formAutofill',
      nameProperty: 'name',
      singleContext: 'field.formAutofill',
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest({
        afterCreateUpdateSuccess: this.afterCreateUpdateSuccess.obs$,
        datatable: this.datatableParams$.pipe(filter(Boolean), skip(1)),
      }).subscribe(({ datatable }) => {
        this.store.loadFormAutofill({
          fields: ['name', 'formId', 'authorization'],
          join: [
            'form||name',
            'formAutofillMappings||autofillId,formFieldKey,widgetRequestId,content',
            'formAutofillMappings.widgetRequest||name',
          ],
          limit: datatable.limit,
          page: datatable.page,
          sort: [datatable.sort],
          ...(datatable.search
            ? {
                search: {
                  searchedFields: ['name'],
                  searchText: datatable.search,
                },
              }
            : {}),
        });
      }),
    );
  }

  public initializeAddEdit(isEdit: boolean): void {
    if (isEdit) {
      this.setupForEdit();
    } else {
      this.setupForAdd();
    }

    const data: IFormAutofillAddEditDialogData = {
      dialogTitle: this.addEditModalTitle(),
      submitButtonText: this.addEditSubmitButtonText(),
      ...(this.isEditDialog()
        ? {
            editData: this.selectedRows()[0],
          }
        : {}),
    };

    this.afterCreateUpdateSuccess.switch(
      this.matDialog
        .open(FormAutofillAddEditDialogComponent, {
          ...FormAutofillAddEditDialogComponent.dialogConfig,
          data,
        })
        .afterClosed()
        .pipe(take(1)),
    );
  }
}
